// The chat allowance (backend app/agent/limits.py). Usage is counted in
// "units": one unit costs what one uncached input token costs on the model in
// use, so units × usd_per_million_units / 1,000,000 is the US$ it can cost.

export type ChatSettingKey =
  | 'AGENT_DAILY_BUDGET'
  | 'AGENT_BUYER_BUDGET_MULTIPLIER'
  | 'AGENT_BUYER_LOOKBACK_DAYS'
  | 'AGENT_SOFT_LIMIT_RATIO';

export interface ChatLimitChange {
  what: 'CHAT_SETTINGS_CHANGED' | 'CHAT_LIMIT_SET' | 'CHAT_LIMIT_REMOVED' | 'CHAT_USAGE_RESET';
  who: string;
  reason: string | null;
  before: Record<string, any> | null;
  after: Record<string, any> | null;
  created_at: string;
}

export interface ChatLimitSettings {
  values: Record<ChatSettingKey, number>;
  file_defaults: Record<ChatSettingKey, number>;
  ranges: Record<ChatSettingKey, [number, number]>;
  model: string;
  usd_per_million_units: string | null;
  updated_at: string | null;
  updated_by: string | null;
  changes: ChatLimitChange[];
}

export interface CustomerChatLimit {
  id: string;
  daily_units: number;
  reason: string;
  expires_at: string | null;
  created_at: string;
  revoked_at: string | null;
  active: boolean;
}

export interface CustomerChat {
  used_today: number;
  allowance: number;
  source: 'customer' | 'buyer' | 'default';
  level: 'ok' | 'soft' | 'hard';
  soft_ratio: number;
  model: string;
  usd_per_million_units: string | null;
  limits: CustomerChatLimit[];
  changes: ChatLimitChange[];
}

export const CHAT_CHANGE_LABELS: Record<ChatLimitChange['what'], string> = {
  CHAT_SETTINGS_CHANGED: 'Changed the defaults',
  CHAT_LIMIT_SET: 'Set their own allowance',
  CHAT_LIMIT_REMOVED: 'Removed their own allowance',
  CHAT_USAGE_RESET: 'Cleared today’s usage',
};

// "≈ $0.02" for a number of units on the model in use; '' when it has no price.
export const unitsInUsd = (units: number, usdPerMillion: string | null): string => {
  if (usdPerMillion == null) return '';
  const usd = (units * Number(usdPerMillion)) / 1_000_000;
  return `≈ $${usd < 0.01 ? usd.toFixed(4) : usd.toFixed(2)}`;
};
